import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@workspace/ui/lib/utils';

import camelRating from '~/assets/img/dubaiexpress/camel-rating.svg';
import type { RatingData } from '~/pages/Product/Citydrinks/ProductV3/components/ProductRatingSummary/interfaces';
import ReviewMediaLightbox from '~/pages/Product/Citydrinks/ProductV3/components/ProductReviews/components/ReviewMediaLightbox';
import type { MediaWithReview } from '~/pages/Product/Citydrinks/ProductV3/components/ProductReviews/interfaces';
import {
  formatCustomerName,
  formatReviewDate,
  getMediaUrl,
} from '~/pages/Product/Citydrinks/ProductV3/components/ProductReviews/utils';
import { userStore } from '~/stores';
import type { ProductReview } from '~/types/Product/interface';

import { REVIEWS_SECTION_ID } from '../constants';

interface Props {
  reviews: ProductReview[];
  totalCount: number;
  ratingData: RatingData | null;
  canLoadMore?: boolean;
  onLoadMore?: () => void;
  canCreateReview?: boolean;
  onCreateReview?: () => void;
}

const PILL_CLASS =
  'flex h-11 cursor-pointer items-center justify-center rounded-full px-5 text-[15px] leading-none transition-colors duration-[120ms] [font-family:inherit]';

/**
 * Review section of the DubaiExpress product page: the review list with a
 * rating breakdown beside it on desktop. Camels stand in for stars.
 */
const ProductDEReviewsView = ({
  reviews,
  totalCount,
  ratingData,
  canLoadMore,
  onLoadMore,
  canCreateReview = false,
  onCreateReview,
}: Props) => {
  const { t } = useTranslation();
  const [lightbox, setLightbox] = useState<{
    isOpen: boolean;
    initialIndex: number;
  }>({ isOpen: false, initialIndex: 0 });

  const allMedia = useMemo<MediaWithReview[]>(
    () =>
      reviews.flatMap((review) =>
        (review.media ?? []).map((media) => ({ media, review })),
      ),
    [reviews],
  );

  const showAddReview = userStore.isAuthorized && canCreateReview;

  if (!reviews.length && !showAddReview && !ratingData) {
    return null;
  }

  const openMedia = (review: ProductReview, mediaIndex: number) => {
    const initialIndex = allMedia.findIndex(
      (item) =>
        item.review === review && item.media === review.media?.[mediaIndex],
    );
    setLightbox({ isOpen: true, initialIndex: Math.max(initialIndex, 0) });
  };

  const maxCount = ratingData
    ? Math.max(...ratingData.distribution.map(({ count }) => count), 1)
    : 1;

  return (
    <section
      id={REVIEWS_SECTION_ID}
      className="mt-12 grid scroll-mt-9 items-start gap-x-16 gap-y-8 min-[860px]:grid-cols-[minmax(0,1fr)_minmax(280px,420px)]"
    >
      <div className="min-w-0">
        <div className="mb-[18px] flex items-baseline gap-2.5">
          <h2 className="m-0 font-semibold text-[#141415] text-[24px] leading-[30px] tracking-[-0.02em]">
            {t('productReviews')}
          </h2>
          <span className="text-[#A0A0A0] text-[24px] leading-[30px]">
            {totalCount}
          </span>
        </div>
        <div className="flex flex-col">
          {reviews.map((review, index) => (
            <div
              key={review.id}
              className={cn(
                index === 0
                  ? 'pb-5'
                  : 'border-0 border-[#EDEDED] border-t border-solid py-5',
              )}
            >
              <div className="flex items-baseline justify-between gap-4">
                <span className="font-semibold text-[#141415] text-[16px] leading-[22px]">
                  {formatCustomerName(review.customer)}
                </span>
                <span className="whitespace-nowrap text-[#A0A0A0] text-[14px] leading-5">
                  {formatReviewDate(review.createdAt)}
                </span>
              </div>
              <p className="m-0 mt-2.5! text-pretty text-[#141415] text-[16px] leading-[22px]">
                {review.content}
              </p>
              {review.media && review.media.length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {review.media.map((media, mediaIndex) => (
                    <button
                      key={media.mediaKey ?? media.url}
                      type="button"
                      onClick={() => openMedia(review, mediaIndex)}
                      className="size-[88px] cursor-pointer overflow-hidden rounded-[10px] border-0 bg-[#F7F7F7] p-0"
                    >
                      <img
                        src={getMediaUrl(media)}
                        alt=""
                        loading="lazy"
                        className="size-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        {(showAddReview || canLoadMore) && (
          <div className="mt-5 flex flex-wrap gap-2">
            {showAddReview && (
              <button
                type="button"
                onClick={onCreateReview}
                className={cn(
                  PILL_CLASS,
                  'border-0 bg-[#E94E1A] font-semibold text-white hover:bg-[#C43F12]',
                )}
              >
                {t('addReview')}
              </button>
            )}
            {canLoadMore && (
              <button
                type="button"
                onClick={onLoadMore}
                className={cn(
                  PILL_CLASS,
                  'border border-[#E1E1E1] border-solid bg-white font-medium text-[#141415] hover:bg-[#F7F6F2]',
                )}
              >
                {t('showMore')}
              </button>
            )}
          </div>
        )}
      </div>
      {ratingData && (
        <div className="max-desktop:hidden">
          <div className="mb-3.5 flex items-center justify-between gap-4">
            <span className="flex items-center gap-2">
              <span className="font-semibold text-[#141415] text-[24px] leading-[30px] tracking-[-0.02em]">
                {ratingData.averageRating}
              </span>
              <img src={camelRating} alt="" className="block h-5 w-auto" />
            </span>
            <span className="font-semibold text-[#8A8A8A] text-[14px] leading-5">
              {t('ratingsCount', { count: ratingData.totalRatings })}
            </span>
          </div>
          <div className="flex flex-col gap-2.5">
            {ratingData.distribution.map(({ stars, count }) => (
              <div key={stars} className="flex items-center gap-3">
                <span className="flex shrink-0 items-center gap-[5px]">
                  <span className="text-[#141415] text-[14px] leading-5">
                    {stars}
                  </span>
                  <img
                    src={camelRating}
                    alt=""
                    className="block h-[13px] w-auto"
                  />
                </span>
                <span className="h-1.5 flex-1 overflow-hidden rounded-[3px] bg-[#EDEDED]">
                  <span
                    className="block h-full rounded-[3px] bg-[#E94E1A]"
                    style={{
                      width: `${Math.max(count ? 2 : 0, Math.round((count / maxCount) * 100))}%`,
                    }}
                  />
                </span>
                <span className="min-w-7 shrink-0 text-right text-[#141415] text-[14px] leading-5">
                  {count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      <ReviewMediaLightbox
        isOpen={lightbox.isOpen}
        onClose={() => setLightbox((state) => ({ ...state, isOpen: false }))}
        allMediaWithReviews={allMedia}
        initialIndex={lightbox.initialIndex}
      />
    </section>
  );
};

export const ProductDEReviews = observer(ProductDEReviewsView);
